/**
 * Non-database utility functions for managing file names and metadata
 * Database operations have been moved to fileManagerUtils.js
 */

/**
 * Add a file to the root folder (no specific folder)
 * @param {string} userId - The user's ID
 * @param {string} fileName - Display name for the file
 * @param {string} fileUrl - Download URL of the file
 * @param {string} uploadSource - Source of upload ('venue_report', 'discussion', etc.)
 * @param {Object} metadata - Additional metadata for the file
 * @returns {Promise<string>} - The file ID that was created
 */
export const addFileToRootFolder = async (userId, fileName, fileUrl, uploadSource, metadata = {}) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            throw new Error('User document not found');
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Create file entry for root folder (no folderId)
        const fileId = `file_root_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const fileData = {
            folderId: null, // Root folder - no specific folder
            name: fileName,
            url: fileUrl,
            storagePath: fileUrl, // Use URL as storage path for external uploads
            order: Object.values(existingFiles).filter(f => f.folderId === null).length,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            protected: false, // Root files are not protected by default
            uploadSource: uploadSource,
            ...metadata // Spread any additional metadata
        };
        
        const updatedFiles = { ...existingFiles, [fileId]: fileData };
        
        await updateDoc(userRef, { files: updatedFiles });
        
        console.log(`✅ Added file "${fileName}" to root folder for user ${userId}`);
        return fileId;
        
    } catch (error) {
        console.error('Error adding file to root folder:', error);
        // Don't throw error - we don't want to break the original upload process
        // Just log it and continue
        return null;
    }
};

/**
 * Helper function to extract file name from URL or generate a meaningful name
 * @param {string} url - File URL
 * @param {string} type - File type ('photo', 'video', 'document')
 * @param {string} source - Upload source
 * @param {number} index - File index if multiple files
 * @returns {string} - Generated file name
 */
export const generateFileName = (url, type, source, index = 0) => {
    try {
        // Try to extract filename from URL
        const urlParts = url.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        const fileName = lastPart.split('?')[0]; // Remove query parameters
        
        if (fileName && fileName.includes('.')) {
            return fileName;
        }
        
        // Generate meaningful name if can't extract from URL
        const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const fileExtension = getFileExtensionFromType(type);
        
        return `${source}_${type}_${timestamp}_${index}${fileExtension}`;
        
    } catch (error) {
        // Fallback name
        return `${source}_${type}_${Date.now()}_${index}`;
    }
};

/**
 * Get file extension based on type
 * @param {string} type - File type
 * @returns {string} - File extension
 */
const getFileExtensionFromType = (type) => {
    switch (type) {
        case 'photo':
        case 'image':
            return '.jpg';
        case 'video':
            return '.mp4';
        case 'document':
            return '.pdf';
        default:
            return '';
    }
};


/**
 * Remove a file from the User Uploads folder
 * @param {string} userId - The user's ID
 * @param {string} fileUrl - The URL of the file to remove
 * @returns {Promise<boolean>} - Whether the file was successfully removed
 */
export const removeFileFromUserUploads = async (userId, fileUrl) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            console.log('User document not found');
            return false;
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Find the file by URL
        const fileToRemove = Object.entries(existingFiles).find(([id, file]) => 
            file.url === fileUrl || file.storagePath === fileUrl
        );
        
        if (!fileToRemove) {
            console.log('File not found in User Uploads folder');
            return false;
        }
        
        const [fileId, fileData] = fileToRemove;
        
        // Remove the file from the files object
        const updatedFiles = { ...existingFiles };
        delete updatedFiles[fileId];
        
        // Update the user document
        await updateDoc(userRef, { files: updatedFiles });
        
        console.log(`✅ Removed file "${fileData.name}" from User Uploads folder for user ${userId}`);
        return true;
        
    } catch (error) {
        console.error('Error removing file from User Uploads:', error);
        return false;
    }
};

/**
 * Remove a file from the root folder
 * @param {string} userId - The user's ID
 * @param {string} fileUrl - The URL of the file to remove
 * @returns {Promise<boolean>} - Whether the file was successfully removed
 */
export const removeFileFromRootFolder = async (userId, fileUrl) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            console.log('User document not found');
            return false;
        }
        
        const userData = userDoc.data();
        const existingFiles = userData.files || {};
        
        // Find the file by URL in root folder (folderId === null)
        const fileToRemove = Object.entries(existingFiles).find(([id, file]) => 
            (file.url === fileUrl || file.storagePath === fileUrl) && file.folderId === null
        );
        
        if (!fileToRemove) {
            console.log('File not found in root folder');
            return false;
        }
        
        const [fileId, fileData] = fileToRemove;
        
        // Remove the file from the files object
        const updatedFiles = { ...existingFiles };
        delete updatedFiles[fileId];
        
        // Update the user document
        await updateDoc(userRef, { files: updatedFiles });
        
        console.log(`✅ Removed file "${fileData.name}" from root folder for user ${userId}`);
        return true;
        
    } catch (error) {
        console.error('Error removing file from root folder:', error);
        return false;
    }
};

/**
 * Get files from the root folder
 * @param {string} userId - The user's ID
 * @returns {Promise<Array>} - Array of files in the root folder
 */
export const getRootFolderFiles = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            return [];
        }
        
        const userData = userDoc.data();
        const files = userData.files || {};
        
        // Get files from root folder (folderId === null)
        const rootFiles = Object.entries(files)
            .filter(([id, file]) => file.folderId === null)
            .map(([id, file]) => ({ id, ...file }))
            .sort((a, b) => a.order - b.order);
        
        return rootFiles;
        
    } catch (error) {
        console.error('Error getting root folder files:', error);
        return [];
    }
};

/**
 * Get user uploads statistics
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} - Statistics about user uploads
 */
export const getUserUploadsStats = async (userId) => {
    try {
        const db = getFirestore();
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            return { hasUserUploadsFolder: false, totalFiles: 0, filesBySource: {} };
        }
        
        const userData = userDoc.data();
        const folders = userData.folders || {};
        const files = userData.files || {};
        
        // Find User Uploads folder
        const userUploadsFolder = Object.entries(folders).find(([id, folder]) => 
            folder.protected === true && folder.name === 'User Uploads'
        );
        
        if (!userUploadsFolder) {
            return { hasUserUploadsFolder: false, totalFiles: 0, filesBySource: {} };
        }
        
        const userUploadsFolderId = userUploadsFolder[0];
        
        // Count files in User Uploads folder
        const userUploadFiles = Object.values(files).filter(file => file.folderId === userUploadsFolderId);
        const filesBySource = {};
        
        userUploadFiles.forEach(file => {
            const source = file.uploadSource || file.migratedFrom || 'unknown';
            filesBySource[source] = (filesBySource[source] || 0) + 1;
        });
        
        return {
            hasUserUploadsFolder: true,
            userUploadsFolderId,
            totalFiles: userUploadFiles.length,
            filesBySource
        };
        
    } catch (error) {
        console.error('Error getting user uploads stats:', error);
        return { hasUserUploadsFolder: false, totalFiles: 0, filesBySource: {} };
    }
};
